/**
 * Course ZPP metadata hook
 *
 * Fetches `zppId`, designation, and preview bullet points from the course document
 * for checkout and program info UI.
 */

import type { Course } from '@physiofit-digital/physiofit_types';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';
import { pickGermanTextList } from '../i18n/pickGermanText';

const courseDocRef = (courseId: string) => doc(db, 'content', 'courses', 'courses', courseId);

type ReadyPayload = {
  courseId: string;
  result:
    | { status: 'error'; message: string }
    | { status: 'ready'; zppId: string; designation: string; patientAppPreviewBulletPointsDe: string[] };
};

export type CourseZppInfoResult =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; zppId: string; designation: string; patientAppPreviewBulletPointsDe: string[] };

/**
 * Loads ZPP id, designation, and German preview bullets for a course id.
 *
 * @param {string} courseId Firestore course document id.
 * @param {number} [feeEur] Fee the patient pays; picks the matching certification where the course has a list.
 * @return {Object} Loading, error, or ready ZPP metadata.
 */
export function useCourseZppInfo(courseId: string, feeEur?: number): CourseZppInfoResult {
  const trimmedId = courseId.trim();
  const [payload, setPayload] = useState<ReadyPayload | null>(null);

  useEffect(() => {
    if (!trimmedId) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const courseSnap = await getDoc(courseDocRef(trimmedId));

        if (cancelled) {
          return;
        }

        if (!courseSnap.exists()) {
          setPayload({
            courseId: trimmedId,
            result: { status: 'error', message: 'Kurs wurde nicht gefunden.' },
          });
          return;
        }

        const course = courseSnap.data() as Course;
        // The admin script marks one certification preferred per fee and app version (ERS-90).
        const certification = course.certifications?.find(
          (entry) => entry.preferred && entry.feeEur === feeEur && (entry.appVersion == null || entry.appVersion === 1),
        );
        const zppId = (certification?.kursId ?? course.zppId)?.trim() ?? '';
        const designation = (certification?.name ?? course.zppName)?.trim() ?? '';
        const patientAppPreviewBulletPointsDe = pickGermanTextList(course.patientAppPreviewBulletPoints);

        setPayload({
          courseId: trimmedId,
          result: { status: 'ready', zppId, designation, patientAppPreviewBulletPointsDe },
        });
      } catch {
        if (!cancelled) {
          setPayload({
            courseId: trimmedId,
            result: {
              status: 'error',
              message: 'Kursdaten konnten nicht geladen werden.',
            },
          });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [trimmedId, feeEur]);

  if (!trimmedId) {
    return { status: 'error', message: 'Keine Kurs-ID angegeben.' };
  }

  if (!payload || payload.courseId !== trimmedId) {
    return { status: 'loading' };
  }

  return payload.result;
}
